import type { RefObject } from "react";
import { useEffect, useState } from "react";

// 반응 속도 (0 ~ 1)
const SPEED = 0.06;
// 모바일 최대 기울기 (deg)
const MAX_ANGLE = 25;

// 아이폰 권한
const OrientationEvent = window.DeviceOrientationEvent as typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<string>;
};

// 최대 이동 범위 (-1 ~ 1)
const limit = (value: number) => Math.min(Math.max(value, -1), 1);

export default function useTiltParallax(containerRef: RefObject<HTMLElement | null>) {
  const [needsPermission, setNeedsPermission] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isMobile = window.matchMedia("(pointer: coarse)").matches;
    setNeedsPermission(isMobile && Boolean(OrientationEvent?.requestPermission));

    // 목표 위치, 현재 위치
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    // 처음 폰을 들고 있던 각도
    let startAngle: number | null = null;

    const onMouseMove = (event: MouseEvent) => {
      target.x = limit((event.clientX / window.innerWidth) * 2 - 1);
      target.y = limit((event.clientY / window.innerHeight) * 2 - 1);
    };

    const onPhoneTilt = (event: DeviceOrientationEvent) => {
      if (event.gamma === null || event.beta === null) return;
      if (startAngle === null) startAngle = event.beta;
      target.x = limit(event.gamma / MAX_ANGLE);
      target.y = limit((event.beta - startAngle) / MAX_ANGLE);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("deviceorientation", onPhoneTilt);

    let frameId = 0;

    const update = () => {
      frameId = requestAnimationFrame(update);
      current.x += (target.x - current.x) * SPEED;
      current.y += (target.y - current.y) * SPEED;
      container.style.setProperty("--tilt-x", current.x.toFixed(4));
      container.style.setProperty("--tilt-y", current.y.toFixed(4));
    };

    const observer = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(frameId);
      if (entry?.isIntersecting) update();
    });
    observer.observe(container);

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("deviceorientation", onPhoneTilt);
    };
  }, []);

  const requestPermission = async () => {
    await OrientationEvent?.requestPermission?.().catch(() => null);
    setNeedsPermission(false);
  };

  return { needsPermission, requestPermission };
}
